import Vue from 'vue'
import toString from 'lodash-es/toString'
import config from 'config'

import i18n from '@vue-storefront/i18n'
import store from '@vue-storefront/core/store'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import { baseFilterProductsQuery, buildFilterProductsQuery, isServer } from '@vue-storefront/core/helpers'
import { currentStoreView, localizedRoute } from '@vue-storefront/core/lib/multistore'
import Composite from '@vue-storefront/core/mixins/composite'
import { Logger } from '@vue-storefront/core/lib/logger'
import { mapGetters, mapActions } from 'vuex'
import onBottomScroll from '@vue-storefront/core/mixins/onBottomScroll'

export default {
  name: 'Category',
  mixins: [Composite, onBottomScroll],
  data () {
    return {
      pagination: {
        perPage: 50,
        current: 0,
        enabled: false
      },
      lazyLoadProductsOnscroll: true
    }
  },
  computed: {
    ...mapGetters({
      getCurrentCategory: 'category/getCurrentCategory',
      getCurrentCategoryProductQuery: 'category/getCurrentCategoryProductQuery',
      getAllCategoryFilters: 'category/getAllCategoryFilters',
      getCategoryBreadcrumbs: 'category/getCategoryBreadcrumbs',
      getCurrentCategoryPath: 'category/getCurrentCategoryPath',
      currentPartner: 'partners/getCurrentPartner',
      metaInformation: 'configs/getMetaInformation'
    }),
    products () {
      return this.$store.getters['product/list']
    },
    productsCounter () {
      return this.products ? this.products.length : 0
    },
    productsTotal () {
      return this.$store.state.product.list.total
    },
    currentQuery () {
      return this.getCurrentCategoryProductQuery
    },
    isCategoryEmpty () {
      return (!(this.products) || this.products.length === 0)
    },
    category () {
      return this.getCurrentCategory
    },
    categoryName () {
      return this.getCurrentCategory ? this.getCurrentCategory.name : ''
    },
    categoryId () {
      return this.getCurrentCategory ? this.getCurrentCategory.entity_id : ''
    },
    filters () {
      return this.getAllCategoryFilters
    },
    breadcrumbs () {
      return this.getCategoryBreadcrumbs
    },
    ogMetaDescription () {
      if (this.metaInformation.find(meta => meta.key === 'og_description')) {
        return this.metaInformation.find(meta => meta.key === 'og_description').value
      }
      return ''
    },
    ogMetaImage () {
      if (this.metaInformation.find(meta => meta.key === 'og_image')) {
        return this.metaInformation.find(meta => meta.key === 'og_image').value
      }
      return ''
    },
    defaultCategoryImageUrl () {
      return this.ogMetaImage ? this.ogMetaImage : ''
    },
    categoryImageUrl () {
      return config.images.baseUrl.replace('/img/', '/') + 'catImg/300/300/resize/' + (this.category.image.substr(0, 1) !== '/' ? this.category.image : this.category.image.slice(1))
    },
    getHostPath () {
      return config.canonical ? config.canonical.url : ''
    }
  },
  preAsyncData ({ store, route }) {
    if (!store.getters['partners/getCurrentPartnerCode']) {
      return
    }
    Logger.log('preAsyncData query setup')()
    const currentProductQuery = store.getters['category/getCurrentCategoryProductQuery']
    const sort = currentProductQuery && currentProductQuery.sort ? currentProductQuery.sort : config.entities.productList.sort
    store.dispatch('category/setSearchOptions', {
      populateAggregations: true,
      store: store,
      route: route,
      current: 0,
      perPage: 50,
      sort,
      filters: config.products.defaultFilters,
      includeFields: config.entities.optimize && isServer ? config.entities.productList.includeFields : null,
      excludeFields: config.entities.optimize && isServer ? config.entities.productList.excludeFields : null,
      append: false,
      partner: route.params.store ? route.params.store : false
    })
  },
  async asyncData ({ store, route, context }) { // this is for SSR purposes to prefetch data
    // metaInfo()'s title/canonical read currentPartner; App.vue's store watcher
    // doesn't fire for the initial route.
    if (route.params.store) {
      await store.dispatch('partners/ensureCurrentPartner', { code: route.params.store, locationCode: route.params.locationCode })
    }
    if (!store.getters['partners/getCurrentPartnerCode']) {
      return
    }
    Logger.info('Entering asyncData in Category Page (core)')()
    try {
      if (context) {
        context.output.cacheTags.add(`category`)
      }
      const defaultFilters = config.products.defaultFilters
      store.dispatch('category/resetFilters')
      EventBus.$emit('filter-reset')
      let parentCategory;
      // If URL for category will contain slug we perform this logic
      if (route.params.slug) {
        parentCategory = await store.dispatch('category/single', {
          key: config.products.useMagentoUrlKeys ? 'url_key' : 'slug',
          value: route.params.slug,
          updateState: false
        })
      }
      // If URL for category will contain url_path we perform this logic
      if (route.params.url_path) {
        parentCategory = await store.dispatch('category/single', {
          key: 'url_path',
          value: route.params.url_path,
          partner: route.params.store ? route.params.store : false,
          updateState: false
        })
      }

      if (parentCategory) {
        await store.dispatch('category/list', {
          parent: parentCategory.entity_id,
          onlyNotEmpty: true,
          level: parentCategory.level + 1
        })
      }

      let query = store.getters['category/getCurrentCategoryProductQuery']
      if (!query.searchProductQuery) {
        let partner = store.getters['partners/getCurrentPartner']
        let category = store.getters['category/getCurrentCategory']
        let sort = 'sum_score:desc'
        if (partner && partner.product_sort === 'sort_range' && category) {
          sort = 'category.position:asc'
        }
        store.dispatch('category/mergeSearchOptions', {
          searchProductQuery: baseFilterProductsQuery(parentCategory, defaultFilters),
          sort: sort
        })
      }

      if (!query.partner) {
        query.partner = route.params.store ? route.params.store : false
      }

      store.state.products_categories.categoriesProducts = [];
      store.state.products_categories.categoriesLoad = false;

      const subloaders = await store.dispatch('category/products', query)
      if (subloaders) {
        await Promise.all(subloaders)
        await EventBus.$emitFilter('category-after-load', { store: store, route: route })
      } else {
        throw new Error('Category query returned empty result')
      }
    } catch (err) {
      Logger.error(err)()
      throw err
    }
  },
  async beforeRouteEnter (to, from, next) {
    if (!isServer && !from.name) { // Loading category products to cache on SSR render
      next(vm => {
        const defaultFilters = config.products.defaultFilters
        let parentCategory = store.getters['category/getCurrentCategory']

        if (parentCategory) {
          store.dispatch('category/list', {
            parent: parentCategory.entity_id,
            onlyNotEmpty: true,
            level: parentCategory.level + 1
          })
        }

        let query = store.getters['category/getCurrentCategoryProductQuery']
        if (!query.searchProductQuery) {
          let partner = store.getters['partners/getCurrentPartner']
          let category = store.getters['category/getCurrentCategory']
          let sort = 'sum_score:desc'
          if (partner && partner.product_sort === 'sort_range' && category) {
            sort = 'category.position:asc'
          }
          store.dispatch('category/mergeSearchOptions', {
            searchProductQuery: baseFilterProductsQuery(parentCategory, defaultFilters),
            sort,
            cacheOnly: true// this is cache only request
          })
        }
        if (!query.partner) {
          query.partner = to.params.store ? to.params.store : false
        }
        store.dispatch('category/products', query)
      })
    } else {
      next()
    }
  },
  beforeMount () {
    this.$bus.$on('filter-changed-category', this.onFilterChanged)
    this.$bus.$on('list-change-sort', this.onSortOrderChanged)
    if (config.usePriceTiers) {
      this.$bus.$on('user-after-loggedin', this.onUserPricesRefreshed)
      this.$bus.$on('user-after-logout', this.onUserPricesRefreshed)
    }
  },
  async mounted () {
    if (!this.category.name) {
      if (this.$route.params.slug) {
        await this.$store.dispatch('category/single', {
          key: config.products.useMagentoUrlKeys ? 'url_key' : 'slug',
          value: this.$route.params.slug
        })
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('list-change-sort', this.onSortOrderChanged)
    this.$bus.$off('filter-changed-category', this.onFilterChanged)
    if (config.usePriceTiers) {
      this.$bus.$off('user-after-loggedin', this.onUserPricesRefreshed)
      this.$bus.$off('user-after-logout', this.onUserPricesRefreshed)
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.validateRoute(to)
    next()
  },
  methods: {
    ...mapActions('category', ['mergeSearchOptions']),
    onBottomScroll () {
      this.pullMoreProducts()
    },
    bottomVisible () {
      const scrollY = Math.ceil(window.scrollY)
      const visible = window.innerHeight
      const pageHeight = document.documentElement.scrollHeight
      const bottomOfPage = visible + scrollY >= pageHeight
      return bottomOfPage || pageHeight < visible
    },
    pullMoreProducts () {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return
      let current = this.getCurrentCategoryProductQuery.current + this.getCurrentCategoryProductQuery.perPage
      this.mergeSearchOptions({
        append: true,
        route: this.$route,
        store: this.$store,
        current
      })
      this.pagination.current = this.getCurrentCategoryProductQuery.current
      this.pagination.perPage = this.getCurrentCategoryProductQuery.perPage
      if (this.getCurrentCategoryProductQuery.current <= this.productsTotal) {
        this.mergeSearchOptions({
          searchProductQuery: buildFilterProductsQuery(this.category, this.filters.chosen)
        })
        return this.$store.dispatch('category/products', this.getCurrentCategoryProductQuery)
      }
    },
    onFilterChanged (filterOption) {
      this.pagination.current = 0
      if (this.filters.chosen[filterOption.attribute_code] && ((toString(filterOption.id) === toString(this.filters.chosen[filterOption.attribute_code].id)) || filterOption.id === this.filters.chosen[filterOption.attribute_code].id)) { // for price filter it's a string
        Vue.delete(this.filters.chosen, filterOption.attribute_code)
      } else {
        Vue.set(this.filters.chosen, filterOption.attribute_code, filterOption)
      }

      let filterQr = buildFilterProductsQuery(this.category, this.filters.chosen)

      const filtersConfig = Object.assign({}, this.filters.chosen) // create a copy because it will be used asynchronously (take a look below)
      this.mergeSearchOptions({
        populateAggregations: false,
        searchProductQuery: filterQr,
        current: this.pagination.current,
        perPage: this.pagination.perPage,
        configuration: filtersConfig,
        append: false,
        includeFields: null,
        excludeFields: null
      })
      this.$store.dispatch('category/products', this.getCurrentCategoryProductQuery).then((res) => {
      }) // because already aggregated
    },
    onSortOrderChanged (param) {
      this.pagination.current = 0
      if (param.attribute) {
        const filtersConfig = Object.assign({}, this.filters.chosen) // create a copy because it will be used asynchronously (take a look below)
        let filterQr = buildFilterProductsQuery(this.category, this.filters.chosen)
        this.mergeSearchOptions({
          sort: param.attribute,
          searchProductQuery: filterQr,
          current: this.pagination.current,
          perPage: this.pagination.perPage,
          configuration: filtersConfig,
          append: false,
          includeFields: null,
          excludeFields: null
        })
        this.$store.dispatch('category/products', this.getCurrentCategoryProductQuery).then((res) => {
        })
      } else {
        this.notify()
      }
    },
    validateRoute (route = this.$route) {
      this.$store.dispatch('category/resetFilters')
      this.$bus.$emit('filter-reset')

      // Around validation route we are checking what parameters was get
      let key, value;
      if (route.params.url_path) {
        key = 'url_path';
        value = route.params.url_path;
      } else if (route.params.slug) {
        key = 'slug';
        value = route.params.slug;
      }

      this.$store.dispatch('category/single', { key: key, value: value }).then(category => {
        if (!category) {
          this.$router.push(this.localizedRoute('/'))
        } else {
          this.pagination.current = 0
          let searchProductQuery = baseFilterProductsQuery(this.getCurrentCategory, config.products.defaultFilters)
          this.$bus.$emit('current-category-changed', this.getCurrentCategoryPath)
          this.mergeSearchOptions({ // base prototype from the asyncData is being used here
            current: this.pagination.current,
            perPage: this.pagination.perPage,
            store: this.$store,
            route: this.$route,
            append: false,
            populateAggregations: true
          })
          if (!this.getCurrentCategoryProductQuery.searchProductQuery) {
            this.mergeSearchOptions({
              searchProductQuery
            })
          }
          this.$store.dispatch('category/products', this.getCurrentCategoryProductQuery)
          this.$bus.$emitFilter('category-after-load', { store: this.$store, route: route })
        }
      }).catch(err => {
        if (err.message.indexOf('query returned empty result') > 0) {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: i18n.t('The product, category or CMS page is not available in Offline mode. Redirecting to Home.'),
            action1: { label: i18n.t('OK') }
          })
          this.$router.push(localizedRoute('/', currentStoreView().storeCode))
        }
      })
    },
    onUserPricesRefreshed () {
      const defaultFilters = config.products.defaultFilters
      this.$store.dispatch('category/single', {
        key: config.products.useMagentoUrlKeys ? 'url_key' : 'slug',
        value: this.$route.params.slug
      }).then((parentCategory) => {
        if (!this.getCurrentCategoryProductQuery.searchProductQuery) {
          this.mergeSearchOptions({
            searchProductQuery: baseFilterProductsQuery(parentCategory, defaultFilters),
            skipCache: true
          })
        }
        this.$store.dispatch('category/products', this.getCurrentCategoryProductQuery)
      })
    }
  },
  metaInfo () {
    let suffix = this.currentPartner.display_mode === 'food'
      ? '. Доставка до 60 хвилин'
      : '. Доставка якнайшвидше або у вибраний час'

    let currentCategoryMeta = [
      {
        vmid: 'description',
        name: 'description',
        content: 'Привеземо Вам усе що забажаєте. У нас в каталозі Ви можете переглянути та замовити страви та товари з найкращих закладів харчування, супермаркетів та гіпермаркетів міста'
      },
      {
        vmid: 'og:type',
        property: 'og:type',
        content: 'product.group'
      },
      {
        vmid: 'og:title',
        property: 'og:title',
        content: this.categoryName
      },
      {
        vmid: 'og:image',
        property: 'og:image',
        content: this.category.image ? this.categoryImageUrl : this.defaultCategoryImageUrl
      },
      {
        vmid: 'og:description',
        property: 'og:description',
        content: this.ogMetaDescription
      }
    ]
    return {
      title: `${this.category.name} з ${this.currentPartner.title}${suffix}`,
      meta: currentCategoryMeta,
      link: [
        {rel: 'canonical', href: `${this.getHostPath}/${this.currentPartner.location_code}/${this.currentPartner.code}/${this.getCurrentCategory.url_path}`}
      ]
    }
  }
}
